import React, { useState } from 'react';
import { Doctor, Staff, Room, ScheduleItem, Appointment, Task } from './SchedulerTypes';
import TimeSlotCell from './TimeSlotCell';
import StickyTableWrapper from './StickyTableWrapper';
import MultiItemSelectionModal from './MultiItemSelectionModal';

interface AllScheduleTableProps {
  timeSlots: string[];
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
  scheduleItems: ScheduleItem[];
  appointments?: Appointment[];
  tasks?: Task[];
  getAppointmentForScheduleItem?: (scheduleItemId: string) => Appointment | undefined;
  getTaskForScheduleItem?: (scheduleItemId: string) => Task | undefined;
  onCellClick: (time: string, personId: string, isDoctor?: boolean) => void;
  onEditItem: (item: ScheduleItem) => void;
  onBookAppointment?: (schedule: ScheduleItem) => void;
  onViewAppointment?: (appointment: Appointment) => void;
  onAssignTask?: (schedule: ScheduleItem) => void;
  onViewTask?: (task: Task) => void;
  onAddScheduleItem?: (item: Omit<ScheduleItem, 'id'>) => void;
  onDeleteScheduleItem?: (id: string) => void;
  currentDate?: string;
}

const AllScheduleTable = ({
  timeSlots,
  doctors,
  staff,
  rooms,
  scheduleItems,
  appointments = [],
  tasks = [],
  getAppointmentForScheduleItem = () => undefined,
  getTaskForScheduleItem = () => undefined,
  onCellClick,
  onEditItem,
  onBookAppointment = () => {},
  onViewAppointment = () => {},
  onAssignTask = () => {},
  onViewTask = () => {},
  onAddScheduleItem,
  onDeleteScheduleItem,
  currentDate,
}: AllScheduleTableProps) => {
  // Sort doctors by company and priority: ENDO (1), TDS (2), SSD (3), TCD (4), EM (5), TCEP (6), others (7), then by priority (0=highest), then by name
  const sortDoctorsByCompanyAndPriority = (a: Doctor, b: Doctor) => {
    const getCompanyPriority = (company?: string) => {
      switch (company?.toUpperCase()) {
        case 'ENDO': return 1;
        case 'TDS': return 2;
        case 'SSD': return 3;
        case 'TCD': return 4;
        case 'EM': return 5;
        case 'TCEP': return 6;
        default: return 7;
      }
    };

    const companyPriorityA = getCompanyPriority(a.company);
    const companyPriorityB = getCompanyPriority(b.company);
    
    // Primary sort by company
    if (companyPriorityA !== companyPriorityB) {
      return companyPriorityA - companyPriorityB;
    }
    
    // Secondary sort by priority within same company (0 is highest priority)
    const priorityA = a.priority || 0;
    const priorityB = b.priority || 0;
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // Tertiary sort by name within same company and priority
    return a.name.localeCompare(b.name);
  };

  // Sort staff by role group, then by priority (0=highest), then by name
  const sortStaffByRoleAndPriority = (a: Staff, b: Staff) => {
    const getRolePriority = (role: string) => {
      const roleLower = role.toLowerCase();
      if (roleLower.includes('dental assistant')) return 1;
      if (roleLower.includes('dental hygienist')) return 2;
      if (roleLower.includes('dental therapist')) return 3;
      if (roleLower.includes('receptionist')) return 4;
      return 5; // Other roles
    };

    // Primary sort by role group
    const roleComparison = getRolePriority(a.role) - getRolePriority(b.role);
    if (roleComparison !== 0) return roleComparison;
    
    // Secondary sort by priority within same role (0 is highest priority)
    const priorityA = a.priority || 0;
    const priorityB = b.priority || 0;
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // Tertiary sort by name within same role and priority
    return a.name.localeCompare(b.name);
  };

  const sortedDoctors = [...doctors].sort(sortDoctorsByCompanyAndPriority);
  const sortedStaff = [...staff].sort(sortStaffByRoleAndPriority);

  // Multi-item selection modal state
  const [multiSelectItems, setMultiSelectItems] = useState<ScheduleItem[]>([]);
  const [multiSelectTime, setMultiSelectTime] = useState<string>('');
  const [isMultiSelectOpen, setIsMultiSelectOpen] = useState(false);

  const handleShowMultiItemSelection = (items: ScheduleItem[], time: string) => {
    setMultiSelectItems(items);
    setMultiSelectTime(time);
    setIsMultiSelectOpen(true);
  };

  const handleSelectItem = (item: ScheduleItem) => {
    onEditItem(item);
  };

  const renderStaffHeader = (person: Doctor | Staff, isDoctor: boolean = false) => {
    return (
      <th key={person.id} className="h-12 px-4 text-center align-middle font-medium text-muted-foreground text-xs">
        <div className="text-sm font-medium">{person.short_name || person.name}</div>
        <div className="text-xs text-muted-foreground">
          {isDoctor ? (person as Doctor).specialization : (person as Staff).role}
        </div>
      </th>
    );
  };

  const renderPersonCell = (time: string, personId: string, isDoctor: boolean) => {
    return (
      <TimeSlotCell
        key={`${time}-${personId}`}
        time={time}
        personId={personId}
        isDoctor={isDoctor}
        scheduleItems={scheduleItems || []}
        appointments={appointments}
        tasks={tasks}
        doctors={doctors}
        staff={staff}
        rooms={rooms}
        getAppointmentForScheduleItem={getAppointmentForScheduleItem}
        getTaskForScheduleItem={getTaskForScheduleItem}
        onCellClick={onCellClick}
        onEditItem={onEditItem}
        onBookAppointment={onBookAppointment}
        onViewAppointment={onViewAppointment}
        onAssignTask={onAssignTask}
        onViewTask={onViewTask}
        onAddScheduleItem={onAddScheduleItem}
        onDeleteScheduleItem={onDeleteScheduleItem}
        currentDate={currentDate}
        onShowMultiItemSelection={handleShowMultiItemSelection}
      />
    );
  };

  return (
    <>
    <StickyTableWrapper>
      <thead className="[&_tr]:border-b">
        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
            <th className="h-12 px-4 text-center align-middle font-medium text-muted-foreground w-16">TIME</th>
            {/* Doctors - sorted by company and priority */}
            {sortedDoctors.map(doctor => renderStaffHeader(doctor, true))}
            {/* Staff - sorted by role and priority */}
            {sortedStaff.map(staffMember => renderStaffHeader(staffMember))}
          </tr>
        </thead>
        <tbody className="[&_tr:last-child]:border-0">
          {timeSlots.map(time => (
            <tr key={time} className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
              <td className="p-4 align-middle font-mono text-xs text-center bg-muted/30">
                {time}
              </td>
              {/* Doctors - sorted by company and priority */}
              {sortedDoctors.map(doctor => renderPersonCell(time, doctor.id, true))}
              {/* Staff - sorted by role and priority */}
              {sortedStaff.map(staffMember => renderPersonCell(time, staffMember.id, false))}
            </tr>
          ))}
      </tbody>
    </StickyTableWrapper>

    {/* Multi-item selection modal */}
    <MultiItemSelectionModal
      isOpen={isMultiSelectOpen}
      onClose={() => setIsMultiSelectOpen(false)}
      items={multiSelectItems}
      onSelectItem={handleSelectItem}
      time={multiSelectTime}
      doctors={doctors}
      staff={staff}
      rooms={rooms}
    />
    </>
  );
};

export default AllScheduleTable;
